import { useEffect, useState, Component } from "react";
import { Routes, Route, Navigate, NavLink, useNavigate } from "react-router-dom";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { auth, db } from "../config/firebase";
import { useBilling } from "../context/BillingContext";
import { useBudget } from "../context/BudgetContext";
import Dashboard from "../pages/Dashboard";
import Transactions from "../pages/Transactions";
import Categories from "../pages/Categories";
import Settings from "../pages/Settings";
import SpendingGroups from "../pages/SpendingGroups";
import LoginPage from "../pages/LoginPage";
import SetupPage from "../pages/SetupPage";
import VerifyEmail from "../pages/VerifyEmail";
import LandingPage from "../pages/LandingPage";
import Runway from "../pages/Runway";
import Departments from "../pages/Departments";
import Team from "../pages/Team";
import Billing from "../pages/Billing";
import BillingSuccess from "../pages/BillingSuccess";
import StartTrial from "../pages/StartTrial";
import JoinWorkspace from "../pages/JoinWorkspace";
import ShareDashboard from "../pages/ShareDashboard";

class ErrorBoundary extends Component {
  state = { hasError: false };
  static getDerivedStateFromError() { return { hasError: true }; }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ minHeight: "100vh", background: "#000", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 12 }}>
          <div style={{ width: 4, height: 4, borderRadius: "50%", background: "#ef4444" }} />
          <span style={{ fontFamily: "var(--mono)", fontSize: 11, color: "#333" }}>something went wrong — reload to continue</span>
        </div>
      );
    }
    return this.props.children;
  }
}

const NAV = [
  { to: "/app",              label: "Dashboard" },
  { to: "/app/transactions", label: "Transactions" },
  { to: "/app/runway",       label: "Runway",       pro: true },
  { to: "/app/departments",  label: "Departments",  pro: true },
  { to: "/app/categories",   label: "Categories",   pro: true },
  { to: "/app/groups",       label: "Groups",       pro: true },
  { to: "/app/team",         label: "Team",         pro: true },
];

const NAV_BOTTOM = [
  { to: "/app/billing",  label: "Billing" },
  { to: "/app/settings", label: "Settings", pro: true },
];

function PaywallRoute({ children }) {
  const { isPro, billingLoading } = useBilling();
  if (billingLoading) return null;
  return isPro ? children : <Navigate to="/app/billing" replace />;
}

function TrialGate({ children }) {
  const { isPro, billingLoading } = useBilling();
  if (billingLoading) return (
    <div style={{ minHeight: "100vh", background: "#000", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ width: 4, height: 4, borderRadius: "50%", background: "#22c55e", animation: "pulse 1s ease infinite" }} />
      <style>{`@keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.2} }`}</style>
    </div>
  );
  if (!isPro) return <Navigate to="/start-trial" replace />;
  return children;
}

function Sidebar({ mobileOpen, onClose }) {
  const { isPro } = useBilling();
  const navigate = useNavigate();

  return (
    <>
      {mobileOpen && <div className="sidebar-backdrop" onClick={onClose} />}
      <div className={`sidebar${mobileOpen ? " sidebar-mobile-open" : ""}`}>
        <div className="sidebar-logo">
          <div className="sidebar-logo-dot" />
          <span className="sidebar-logo-text">Greenline</span>
        </div>

        {NAV.map(({ to, label, pro }) => {
          const locked = pro && !isPro;
          if (locked) {
            return (
              <div
                key={to}
                className="nav-item nav-item-locked"
                onClick={() => { navigate("/app/billing"); onClose(); }}
              >
                <div className="nav-dot" />
                <span className="nav-label">{label}</span>
                <span className="nav-lock" title="Pro plan required">&#x1F512;</span>
              </div>
            );
          }
          return (
            <NavLink
              key={to}
              to={to}
              end={to === "/app"}
              onClick={onClose}
              className={({ isActive }) => `nav-item${isActive ? " active" : ""}`}
            >
              <div className="nav-dot" />
              <span className="nav-label">{label}</span>
            </NavLink>
          );
        })}

        <div className="sidebar-spacer" />

        {NAV_BOTTOM.map(({ to, label, pro }) => {
          const locked = pro && !isPro;
          if (locked) {
            return (
              <div
                key={to}
                className="nav-item nav-item-locked"
                onClick={() => { navigate("/app/billing"); onClose(); }}
              >
                <div className="nav-dot" />
                <span className="nav-label">{label}</span>
                <span className="nav-lock" title="Pro plan required">&#x1F512;</span>
              </div>
            );
          }
          return (
            <NavLink
              key={to}
              to={to}
              onClick={onClose}
              className={({ isActive }) => `nav-item${isActive ? " active" : ""}`}
            >
              <div className="nav-dot" />
              <span className="nav-label">{label}</span>
            </NavLink>
          );
        })}
      </div>
    </>
  );
}

function Topbar({ user, onMenuToggle }) {
  const navigate = useNavigate();

  const handleLogOut = async () => {
    await signOut(auth);
    navigate("/login");
  };

  const now = new Date();
  const dateStr = `$ ${now.toLocaleDateString("en-US", { month: "short" }).toLowerCase()} ${now.getFullYear()}`;

  return (
    <div className="topbar">
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <button className="hamburger-btn" onClick={onMenuToggle} aria-label="Toggle menu">
          <span /><span /><span />
        </button>
        <span className="mono" style={{ fontSize: 11, color: "#333" }}>{dateStr}</span>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <div style={{ width: 20, height: 20, borderRadius: "50%", background: "#0a1a0a", border: "1px solid #22c55e22", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <div style={{ width: 6, height: 6, borderRadius: "50%", background: "#22c55e" }} />
          </div>
          <span style={{ fontSize: 11, color: "#444" }}>
            {user?.displayName?.split(" ")[0] || user?.email?.split("@")[0]}
          </span>
        </div>
        <div style={{ width: 1, height: 12, background: "#1a1a1a" }} />
        <button
          onClick={handleLogOut}
          style={{ background: "none", border: "none", color: "#333", fontSize: 11, cursor: "pointer", padding: 0 }}
          onMouseEnter={e => e.target.style.color = "#ef4444"}
          onMouseLeave={e => e.target.style.color = "#333"}
        >
          sign out
        </button>
      </div>
    </div>
  );
}

function AppShell({ user }) {
  const { viewerToast } = useBudget();
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <div className="app-container">
      <Sidebar mobileOpen={mobileOpen} onClose={() => setMobileOpen(false)} />
      <div className="main-content">
        <Topbar user={user} onMenuToggle={() => setMobileOpen(o => !o)} />
        {viewerToast && (
          <div style={{
            position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)",
            background: "#0d0d0d", border: "1px solid #1a1a1a", borderRadius: 6,
            padding: "8px 16px", fontFamily: "var(--mono)", fontSize: 11, color: "#555",
            zIndex: 1000, pointerEvents: "none",
          }}>
            read-only access
          </div>
        )}
        <div className="page-content">
          <Routes>
            <Route path="/"             element={<Dashboard />} />
            <Route path="/transactions" element={<Transactions />} />
            <Route path="/runway"       element={<PaywallRoute><Runway /></PaywallRoute>} />
            <Route path="/departments"  element={<PaywallRoute><Departments /></PaywallRoute>} />
            <Route path="/categories"   element={<PaywallRoute><Categories /></PaywallRoute>} />
            <Route path="/groups"       element={<PaywallRoute><SpendingGroups /></PaywallRoute>} />
            <Route path="/team"         element={<PaywallRoute><Team /></PaywallRoute>} />
            <Route path="/billing"      element={<Billing />} />
            <Route path="/settings"     element={<PaywallRoute><Settings /></PaywallRoute>} />
            <Route path="*"             element={<Navigate to="/app" replace />} />
          </Routes>
        </div>
      </div>
    </div>
  );
}

export default function App() {
  const [user, setUser] = useState(undefined);
  const [onboarded, setOnboarded] = useState(null);
  const { isPro, billingLoading } = useBilling();

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (firebaseUser) => {
      setUser(firebaseUser ?? null);
      if (firebaseUser) {
        const snap = await getDoc(doc(db, "users", firebaseUser.uid));
        setOnboarded(snap.exists() && snap.data()?.onboarded === true);
      } else {
        setOnboarded(null);
      }
    });
    return unsub;
  }, []);

  const spinner = (
    <div style={{ minHeight: "100vh", background: "#000", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ width: 4, height: 4, borderRadius: "50%", background: "#22c55e", animation: "pulse 1s ease infinite" }} />
      <style>{`@keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.2} }`}</style>
    </div>
  );

  if (user === undefined || (user && onboarded === null)) return spinner;
  // Email/password accounts must confirm their address before accessing the app.
  // Google sign-ins already arrive with emailVerified === true.
  if (user && !user.emailVerified) return <VerifyEmail user={user} />;
  if (user && onboarded === false) return <SetupPage uid={user.uid} onComplete={() => setOnboarded(true)} />;

  return (
    <Routes>
      <Route path="/"               element={<LandingPage />} />
      <Route path="/login"          element={user ? <Navigate to="/app" replace /> : <LoginPage />} />
      <Route path="/start-trial"    element={
        !user ? <Navigate to="/login" replace />
        : billingLoading ? spinner
        : isPro ? <Navigate to="/app" replace />
        : <StartTrial />
      } />
      <Route path="/join"           element={<JoinWorkspace />} />
      <Route path="/billing/success" element={<BillingSuccess />} />
      <Route path="/share/:token"    element={<ShareDashboard />} />
      <Route path="/app/*"          element={
        user
          ? <ErrorBoundary><TrialGate><AppShell user={user} /></TrialGate></ErrorBoundary>
          : <Navigate to="/login" replace />
      } />
      <Route path="*"               element={<Navigate to="/" replace />} />
    </Routes>
  );
}